// Mirror of backend/badges.py BADGE_REGISTRY — keep 1:1.
// ONE registry, referenced by stable IDs only. Adding a badge later = a config entry here
// + backend, never a new component.
//
// TWO FAMILIES render separately:
//   VERIFICATION = "what we checked" (safety signal). ALWAYS first.
//   RECOGNITION  = "who someone is to us" (awarded). Rendered after a visible gap.
// Everything else is REPUTATION and trails recognition.

export const BADGE_REGISTRY = {
  verified_id:      { label: "ID Verified", short: "ID", icon: "shield-check", family: "verification",
                      tooltip: "Identity verified through Stripe Identity. A real, documented person is behind this account." },
  dealer:           { label: "Dealer Verified", short: "Dealer", icon: "briefcase", family: "verification",
                      tooltip: "Verified licensed dealer — the business is licensed and its FFL was checked against the federal registry." },
  business_pending: { label: "Business Pending", short: "Pending", icon: "clock", family: "verification",
                      tooltip: "Business application submitted and under review — not yet approved." },
  ffl:              { label: "FFL", short: "FFL", icon: "file-check", family: "verification",
                      tooltip: "Federal Firearms License verified against the actual license. Type shown." },
  sot:              { label: "SOT", short: "SOT", icon: "file-check", family: "verification",
                      tooltip: "Special Occupational Taxpayer — this is what permits NFA items. SOT Class 2 = manufacturer/dealer of NFA, Class 3 = dealer of NFA." },
  founding_member:  { label: "Founding Member", short: "Founding", icon: "medal", family: "recognition",
                      tooltip: "One of 100 founding seats per pool. Seat numbers are permanent and retired for good if the account closes." },
  ambassador:       { label: "Ambassador", short: "Ambassador", icon: "megaphone", family: "recognition",
                      tooltip: "Recognition badge. Commercial terms, if any, are set on the member's account, not by this badge." },
  beta_tester:      { label: "Beta Tester", short: "Beta", icon: "terminal", family: "recognition",
                      tooltip: "Earned by sending feedback through the beta. This person told us what was broken." },
  pro:              { label: "Pro", short: "Pro", icon: "star", family: "reputation",
                      tooltip: "Active Pro membership — supports the platform and unlocks seller perks." },
  rating:           { label: "Rating", short: "Rating", icon: "thumbs-up", family: "reputation",
                      tooltip: "Positive feedback rate across rated sales. Shown once a minimum sample exists." },
  top_rated:        { label: "Top Rated", short: "Top Rated", icon: "award", family: "reputation",
                      tooltip: "Feedback consistently above the platform's Top Rated threshold." },
  sales_25:         { label: "25+ Sales", short: "25+", icon: "trending-up", family: "reputation", tooltip: "Completed 25 or more sales on OpticSwap." },
  sales_50:         { label: "50+ Sales", short: "50+", icon: "trending-up", family: "reputation", tooltip: "Completed 50 or more sales on OpticSwap." },
  sales_75:         { label: "75+ Sales", short: "75+", icon: "trending-up", family: "reputation", tooltip: "Completed 75 or more sales on OpticSwap." },
  sales_100:        { label: "100+ Sales", short: "100+", icon: "trending-up", family: "reputation", tooltip: "Completed 100 or more sales on OpticSwap." },
  fast_shipper:     { label: "Fast Shipper", short: "Fast Ship", icon: "zap", family: "reputation",
                      tooltip: "Ships quickly — median ship time under the platform threshold. Activates once sales are flowing." },
  quick_reply:      { label: "Quick Reply", short: "Quick Reply", icon: "message-square", family: "reputation",
                      tooltip: "Replies quickly — low median response time. Activates once messaging data exists." },
  // LISTINGS MILESTONES — Steel/Platinum treatment (see FIXED_CHIP). Highest tier only, profile shelf only.
  listings_10:      { label: "10 Listings", short: "10", icon: "listings-diamond", family: "reputation", tooltip: "Published 10 or more listings on OpticSwap." },
  listings_25:      { label: "25 Listings", short: "25", icon: "listings-diamond", family: "reputation", tooltip: "Published 25 or more listings on OpticSwap." },
  listings_50:      { label: "50 Listings", short: "50", icon: "listings-diamond", family: "reputation", tooltip: "Published 50 or more listings on OpticSwap." },
  listings_100:     { label: "100 Listings", short: "100", icon: "listings-diamond", family: "reputation", tooltip: "Published 100 or more listings on OpticSwap." },
  listings_250:     { label: "250 Listings", short: "250", icon: "listings-diamond", family: "reputation", tooltip: "Published 250 or more listings on OpticSwap." },
  listings_500:     { label: "500 Listings", short: "500", icon: "listings-diamond", family: "reputation", tooltip: "Published 500 or more listings on OpticSwap." },
};

export const BADGE_FAMILY = Object.fromEntries(
  Object.entries(BADGE_REGISTRY).map(([id, m]) => [id, m.family || "reputation"])
);
export const FAMILY_ORDER = ["verification", "recognition", "reputation"];
export function badgeFamily(id) { return BADGE_FAMILY[id] || "reputation"; }

// Profile shelf order — grouped by family (verification -> recognition -> reputation).
export const PROFILE_ORDER = [
  "verified_id", "dealer", "business_pending", "ffl", "sot",
  "founding_member", "ambassador", "beta_tester",
  "pro", "sales_100", "sales_75", "sales_50", "sales_25",
  "listings_500", "listings_250", "listings_100", "listings_50", "listings_25", "listings_10",
  "rating", "top_rated", "fast_shipper", "quick_reply",
];

// Listing-card cap: max 3, fixed priority (verification first).
export const CARD_PRIORITY = ["verified_id", "dealer", "ffl", "founding_member", "ambassador", "beta_tester", "top_rated", "pro", "fast_shipper", "quick_reply"];
export const CARD_MAX = 3;

// Split a badge list into {verification, recognition, reputation} in PROFILE_ORDER.
export function groupBadges(list) {
  const byId = Object.fromEntries((list || []).map((b) => [b.id, b]));
  const ordered = PROFILE_ORDER.filter((id) => byId[id]).map((id) => byId[id]);
  const out = { verification: [], recognition: [], reputation: [] };
  ordered.forEach((b) => { out[badgeFamily(b.id)].push(b); });
  return out;
}

// Card list: cap 3 in priority order; a RECOGNITION badge never appears without a
// VERIFICATION badge beside it (the safety signal always leads).
export function cardBadges(list) {
  const byId = Object.fromEntries((list || []).map((b) => [b.id, b]));
  let picked = CARD_PRIORITY.filter((id) => byId[id]).map((id) => byId[id]);
  const hasVerification = picked.some((b) => badgeFamily(b.id) === "verification");
  if (!hasVerification) picked = picked.filter((b) => badgeFamily(b.id) !== "recognition");
  return picked.slice(0, CARD_MAX);
}

// Steel/Platinum — the Listings milestone treatment (metallic, distinct from the green/blue/gold tones).
export const STEEL_CHIP = { bg: "#41474E", border: "#AEB8C2", text: "#EAEEF2" };

// FIXED identity colors — a badge's colour means the same thing on every account.
// Verification family is GREEN: olive ID / emerald Dealer+FFL (related, because both are
// "what we checked"). Recognition: gold Founding / burnt-orange Ambassador. Beta Tester renders
// in its own terminal style (see Badges.jsx). Reputation badges fall back to the account tone.
// NOTE: #1f6ba8 is NO LONGER a badge colour — it is reserved for EXTERNAL·BUSINESS conversations.
export const FIXED_CHIP = {
  verified_id:     { bg: "#4e8a1f", text: "#ffffff" },
  dealer:          { bg: "#1f7a4d", border: "#2fa268", text: "#dcf0e5" },
  ffl:             { bg: "#1f7a4d", border: "#2fa268", text: "#dcf0e5" },
  founding_member: { bg: "#14110A", border: "#55451C", text: "#C8A64E" },
  ambassador:      { bg: "#c2571f", text: "#ffffff" },
  // Listings milestones — Steel/Platinum: gunmetal fill, bright platinum rim + text.
  listings_10:     STEEL_CHIP,
  listings_25:     STEEL_CHIP,
  listings_50:     STEEL_CHIP,
  listings_100:    STEEL_CHIP,
  listings_250:    STEEL_CHIP,
  listings_500:    STEEL_CHIP,
};

// Reputation-badge tones (account-driven). Verification/recognition use FIXED_CHIP instead.
export const BADGE_TONE_CLASSES = {
  green: "bg-olive text-white border-olive-bright",
  blue: "bg-[hsl(205_78%_38%)] text-white border-[hsl(205_85%_62%)]",
  gold: "bg-[hsl(41_88%_33%)] text-[hsl(45_95%_90%)] border-[hsl(45_90%_62%)]",
};

export function badgeTone(id, accountTone) {
  if (id === "founding_member") return "gold";
  return accountTone === "blue" ? "blue" : "green";
}

// A badge renders dashed/muted when it's provisional or lapsed:
// business_pending (under review) or an FFL/SOT whose license has expired.
export function badgeDashed(badge) {
  if (!badge) return false;
  if (badge.id === "business_pending") return true;
  if ((badge.id === "ffl" || badge.id === "sot") && badge.value?.expired) return true;
  return false;
}

function pad3(n) { return String(n).padStart(3, "0"); }
function pad2(n) { return String(n).padStart(2, "0"); }

// Display text for a computed badge {id, value?} at a given size ("full" | "compact").
export function badgeLabel(badge, size = "full") {
  const meta = BADGE_REGISTRY[badge.id];
  if (!meta) return "";
  const v = badge.value || {};
  const compact = size === "compact";
  if (badge.id.startsWith("listings_")) {
    const n = badge.id.split("_")[1];
    return compact ? n : `${n} LISTINGS`;
  }
  switch (badge.id) {
    case "rating":
      return compact ? `${v.pct}%` : `${v.pct}% of ${v.count}`;
    case "founding_member":
      return compact ? `Founding #${pad3(v.number)}` : `Founding #${pad3(v.number)} / ${v.cap || 100}`;
    case "dealer":
      return compact ? "Dealer" : (v.level && v.level !== "standard" ? `Dealer Verified · ${v.level}` : "Dealer Verified");
    case "ffl":
      return v.type ? (!compact && v.expired ? `FFL ${v.type} · expired` : `FFL ${v.type}`) : "FFL";
    case "sot":
      return v.class ? (!compact && v.expired ? `SOT ${v.class} · expired` : `SOT ${v.class}`) : "SOT";
    case "fast_shipper":
      return compact ? "Fast Ship" : (v.hours != null ? `Fast Shipper · ${v.hours}h` : "Fast Shipper");
    case "quick_reply":
      return compact ? "Quick Reply" : (v.hours != null ? `Quick Reply · ${v.hours}h` : "Quick Reply");
    case "verified_id":
      return compact ? "ID" : (v.year ? `ID Verified '${String(v.year).slice(-2)}` : "ID Verified");
    default:
      return compact ? meta.short : meta.label;
  }
}

export function badgeTooltip(badge) {
  const meta = BADGE_REGISTRY[badge.id];
  return meta ? meta.tooltip : "";
}
